import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

// Layout & Komponen Shared (Digunakan Kembali)
import AdminLayout from '../../../layouts/AdminLayout';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import FullPageSpinner from '../../../shared/components/spinner/FullPageSpinner';
// Komponen generik yang dapat digunakan kembali
import SearchComponent from '../../user-management/components/SearchComponent';
import PaginationComponent from '../../user-management/components/PaginationComponent';

// Komponen, Hooks, dan Tipe baru untuk V4
import { useGetUsersV4 } from '../hooks/useGetUsersV4';
import { useDeleteUserV4 } from '../hooks/useDeleteUserV4';
import UsersTableV4 from '../components/UsersTableV4';
import CreateUserDialogV4 from '../components/CreateUserDialogV4';
import UpdateUserDialogV4 from '../components/UpdateUserDialogV4';
import ErrorState from '../components/ErrorState';
import { DeleteUserV4Schema } from '../schemas/DeleteUserV4Schema';
import { UserV4 } from '../schemas/UserV4Schema';
// *** BARU: Impor dialog-dialog fitur yang hilang ***
import AssignPropertyDialogV4 from '../components/AssignPropertyDialogV4';
import ViewUserPropertiesDialogV4 from '../components/ViewUserPropertiesDialogV4';
import AssignRoleDialogV4 from '../components/AssignRoleDialogV4';

/**
 * Halaman utama untuk manajemen pengguna (Versi 4 Final).
 * Mencakup fungsionalitas CRUD penuh dan manajemen properti pengguna.
 */
const UserManagementPagesV4: React.FC = () => {
    // State untuk Pagination & Filter
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [searchQuery, setSearchQuery] = useState<string>('');

    // State untuk Dialogs
    const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
    const [showUpdateDialog, setShowUpdateDialog] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<UserV4 | null>(null); // Menggunakan objek User lengkap

    // *** BARU: State untuk dialog fitur properti & role ***
    const [showAssignPropertyDialog, setShowAssignPropertyDialog] = useState<boolean>(false);
    const [showViewPropertiesDialog, setShowViewPropertiesDialog] = useState<boolean>(false);
    const [showAssignRoleDialog, setShowAssignRoleDialog] = useState<boolean>(false);

    // ====================================================================
    // Data Fetching dengan React Query (V4)
    // ====================================================================
    const {
        data: usersResponse,
        isLoading,
        isError,
        error,
        refetch,
        isFetching,
    } = useGetUsersV4({
        // Dulu `filter: { name }` sehingga pencarian hanya kena kolom name.
        // `search` mencocokkan name, email, dan phone sekaligus di backend.
        ...(searchQuery && { search: searchQuery }),
        include: 'properties,groups,company,roles', // Meminta semua relasi yang relevan
        limit: perPage,
        page: currentPage,
        orderBy: 'created_at',
        sort: 'desc',
    });

    // ====================================================================
    // Sinkronisasi State 'selectedUser'
    // ====================================================================
    // Efek ini memastikan bahwa data di dalam dialog yang terbuka (selectedUser)
    // selalu sinkron dengan data terbaru dari `usersResponse`.
    useEffect(() => {
        if (selectedUser && usersResponse?.data) {
            const updatedUser = usersResponse.data.find(user => user.id === selectedUser.id);
            if (updatedUser) {
                setSelectedUser(updatedUser);
            }
        }
    }, [usersResponse, selectedUser]);


    // ====================================================================
    // Mutasi Data dengan React Query (V4)
    // ====================================================================
    const { mutate: deleteUser } = useDeleteUserV4();

    // ====================================================================
    // Handlers
    // ====================================================================

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setPerPage(Number(e.target.value));
        setCurrentPage(1);
    };
    const handleSearch = (query: string) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    // Handlers untuk Dialog CRUD
    const handleOpenCreateDialog = () => setShowCreateDialog(true);
    const handleCloseCreateDialog = () => setShowCreateDialog(false);

    const handleOpenUpdateDialog = (user: UserV4) => {
        setSelectedUser(user);
        setShowUpdateDialog(true);
    };
    const handleCloseUpdateDialog = () => {
        setShowUpdateDialog(false);
        setSelectedUser(null);
    };

    // *** BARU: Handlers untuk Dialog Properti ***

    const handleOpenAssignPropertyDialog = (user: UserV4) => {
        setSelectedUser(user);
        setShowAssignPropertyDialog(true);
    };
    const handleCloseAssignPropertyDialog = () => {
        setShowAssignPropertyDialog(false);
        setSelectedUser(null);
    };

    // Handler untuk Assign Role Dialog
    const handleOpenAssignRoleDialog = (user: UserV4) => {
        setSelectedUser(user);
        setShowAssignRoleDialog(true);
    };
    const handleCloseAssignRoleDialog = () => {
        setShowAssignRoleDialog(false);
        setSelectedUser(null);
    };

    const handleOpenViewPropertiesDialog = (user: UserV4) => {
        setSelectedUser(user);
        setShowViewPropertiesDialog(true);
    };
    const handleCloseViewPropertiesDialog = () => {
        setShowViewPropertiesDialog(false);
        setSelectedUser(null);
    };

    const handleDelete = (userId: string) => {
        const validation = DeleteUserV4Schema.safeParse({ id: userId });
        if (!validation.success) {
            toast.error('Invalid User ID format.');
            return;
        }

        Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                const toastId = 'delete-user-v4';
                toast.loading('Deleting user...', { id: toastId });

                deleteUser(validation.data.id, {
                    onSuccess: () => toast.success('User has been deleted.', { id: toastId }),
                    onError: (err) => {
                        const errorMessage = (err as any)?.response?.data?.message || 'Failed to delete user.';
                        toast.error(errorMessage, { id: toastId });
                    }
                });
            }
        });
    };

    // ====================================================================
    // Render Logic
    // ====================================================================

    const renderContent = () => {
        if (isLoading) return <FullPageSpinner />;
        if (isError) return <ErrorState error={error} onRetry={refetch} />;

        if (usersResponse) {
            return (
                <div className="table-container vh-100">
                    <div className="row g-3 align-items-center mb-4">
                        {/* Dropdown untuk jumlah data per halaman */}
                        {/* col-md-auto membuat lebarnya sesuai konten di desktop */}
                        <div className="col-12 col-md-auto">
                            <select className="form-select form-control" value={perPage} onChange={handlePerPageChange}>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>

                        {/* Komponen Search yang akan mengisi sisa space */}
                        {/* col-md membuat kolom ini mengambil sisa ruang di desktop */}
                        <div className="col-12 col-md">
                            <SearchComponent queryKey={searchQuery} onSearch={handleSearch} placeholder="Search name, email, or phone..." />
                        </div>

                        {/* Spinner loading */}
                        {isFetching && !isLoading && (
                            <div className="col-auto">
                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}

                        {/* Tombol Aksi */}
                        <div className="col-12 col-md-auto">
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-create']} asComponent>
                                <button className="btn btn-primary w-100" onClick={handleOpenCreateDialog}>
                                    Create User
                                </button>
                            </RequireAuth>
                        </div>
                    </div>

                    <UsersTableV4
                        users={usersResponse.data}
                        onUpdate={handleOpenUpdateDialog}
                        onDelete={handleDelete}
                        onAssignProperty={handleOpenAssignPropertyDialog}
                        onViewProperties={handleOpenViewPropertiesDialog}
                        onAssignRole={handleOpenAssignRoleDialog}
                    />

                    <PaginationComponent
                        totalPages={usersResponse.pagination.last_page}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                </div>
            );
        }

        return null;
    };

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                {renderContent()}
            </div>

            {/* Dialog CRUD */}
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-create']} asComponent>
                <CreateUserDialogV4 show={showCreateDialog} onClose={handleCloseCreateDialog} />
            </RequireAuth>

            <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-update']} asComponent>
                {selectedUser && (
                    <UpdateUserDialogV4
                        show={showUpdateDialog}
                        onClose={handleCloseUpdateDialog}
                        userId={selectedUser.id}
                    />
                )}
            </RequireAuth>

            {/* *** BARU: Dialog-dialog fitur properti *** */}
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-assign-property']} asComponent>
                <AssignPropertyDialogV4
                    show={showAssignPropertyDialog}
                    onClose={handleCloseAssignPropertyDialog}
                    user={selectedUser}
                />
            </RequireAuth>

            <ViewUserPropertiesDialogV4
                show={showViewPropertiesDialog}
                onClose={handleCloseViewPropertiesDialog}
                user={selectedUser}
            />

            {/* Dialog Assign Role */}
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['user-assign-role']} asComponent>
                <AssignRoleDialogV4
                    show={showAssignRoleDialog}
                    onClose={handleCloseAssignRoleDialog}
                    user={
                        selectedUser
                            ? {
                                id: selectedUser.id,
                                name: selectedUser.name,
                                roles: Array.isArray(selectedUser.roles)
                                    ? selectedUser.roles.map(role => role.name)
                                    : [],
                            }
                            : null
                    }
                    refetchUser={refetch} // Optional, if you want to refetch user data after role assignment
                />
            </RequireAuth>
        </AdminLayout>
    );
};

export default UserManagementPagesV4;